import { Box, Button, Card, Container, Flex, Heading, Text, TextField } from "@radix-ui/themes";
import { type FormEvent, useState } from "react";
import logo from "../../assets/logo.png";
import Alert from "../shared/Alert";
import Footer from "../shared/Footer";

interface ForgotPasswordViewProps {
    onBackToLogin: () => void;
}

const ForgotPasswordView = ({ onBackToLogin }: ForgotPasswordViewProps) => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch("/auth/backend/forgot-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error_description || "Failed to send reset email");
            }

            setSuccess(true);
        } catch (err: any) {
            console.error("Failed to request password reset", err);
            setError(err.message || "An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container size="1" p="4">
            <Card size="4">
                <Flex
                    direction="column"
                    gap="4"
                    align="center"
                    style={{
                        textAlign: "center",
                        marginBottom: "24px",
                    }}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        style={{
                            width: 64,
                            height: 64,
                            objectFit: "contain",
                        }}
                    />
                    <Heading size="6">Reset Password</Heading>
                    <Text color="gray" size="2">
                        Enter your email to receive a password reset link
                    </Text>
                </Flex>

                {success ? (
                    <Flex direction="column" gap="4">
                        <Alert type="success" message="If an account exists with this email, you will receive a password reset link shortly." />
                        <Button
                            variant="soft"
                            onClick={onBackToLogin}
                            style={{
                                cursor: "pointer",
                            }}
                        >
                            Back to Login
                        </Button>
                    </Flex>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Flex direction="column" gap="4">
                            {error && <Alert type="error" message={error} />}

                            <Box>
                                <Text
                                    as="label"
                                    size="2"
                                    weight="bold"
                                    mb="1"
                                    style={{
                                        display: "block",
                                    }}
                                >
                                    Email Address
                                </Text>
                                <TextField.Root
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    type="email"
                                    required
                                />
                            </Box>

                            <Button
                                type="submit"
                                loading={loading}
                                style={{
                                    cursor: "pointer",
                                }}
                            >
                                Send Reset Link
                            </Button>

                            <Button
                                variant="ghost"
                                type="button"
                                onClick={onBackToLogin}
                                style={{
                                    cursor: "pointer",
                                }}
                            >
                                Back to Login
                            </Button>
                        </Flex>
                    </form>
                )}
            </Card>
            <Footer />
        </Container>
    );
};

export default ForgotPasswordView;
